/**
 * A piece type's param form, built from its param specs (D22,
 * `lib/playout/paramForm.ts`), each param a `ParamControl` (the one control
 * the rundown editor's piece form uses too): a string as a text field (with
 * `enum`, a picker), a number or integer the same (a numeric `enum` is a
 * picker too), a boolean as a checkbox, an object as a group of its own
 * fields, and a list as its items with add and remove — as deep as the spec
 * goes. And the mode: play now into the on-air part, or queue as the next part.
 *
 * The values are checked as the production will check them before anything
 * is sent; each error shows beside its field.
 */
import type { JsonValue } from '@norna/domain/primitives.js';
import { type FormEvent, useState } from 'react';
import { type Draft, type ParamField, parseParams } from '../../lib/playout/paramForm.js';
import { ParamControl } from '../params/ParamControl.js';
import { Button } from '../ui/button.js';

export type AdlibParamFormProps = {
  readonly title: string;
  readonly fields: readonly ParamField[];
  readonly mode: 'now' | 'queue';
  readonly disabled: boolean;
  readonly onSubmit: (input: { readonly params: Record<string, JsonValue>; readonly mode: 'now' | 'queue' }) => void;
  readonly onCancel: () => void;
};

export function AdlibParamForm({ title, fields, mode, disabled, onSubmit, onCancel }: AdlibParamFormProps) {
  const [drafts, setDrafts] = useState<Readonly<Record<string, Draft>>>(() => Object.fromEntries(fields.map((f) => [f.name, f.initial])));
  const [chosenMode, setMode] = useState(mode);
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseParams(fields, drafts);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    onSubmit({ params: parsed.params, mode: chosenMode });
  };
  return (
    <form className="grid gap-3 rounded-md border bg-card p-3" onSubmit={submit} data-testid="adlib-param-form" aria-label={`Adlib ${title}`}>
      <p className="text-sm font-medium">{title}</p>
      {fields.map((field) => (
        <ParamControl
          key={field.name}
          idPrefix="adlib-param"
          path={field.name}
          label={field.label}
          spec={field.spec}
          draft={drafts[field.name] ?? field.initial}
          errors={errors}
          onChange={(next) => setDrafts({ ...drafts, [field.name]: next })}
        />
      ))}
      <fieldset className="flex gap-4 text-sm">
        <legend className="sr-only">When</legend>
        <label className="flex items-center gap-1.5">
          <input type="radio" name="adlib-mode" checked={chosenMode === 'now'} onChange={() => setMode('now')} />
          Now
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" name="adlib-mode" checked={chosenMode === 'queue'} onChange={() => setMode('queue')} />
          Queue as next
        </label>
      </fieldset>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={disabled} data-testid="adlib-submit">
          {chosenMode === 'now' ? 'Play now' : 'Queue'}
        </Button>
      </div>
    </form>
  );
}
